import _ from 'lodash';

import APIClient from '../lib/APIClient';
import IblocksAPIClient from '../lib/IblocksAPIClient';
import Fare from '../records/Fare';
import { resolveAll, resolve } from '../helpers/LinkMapper';
import { SmartCarnetTicketCodes, SmartCarnetTicketStatus } from '../constants/AppConstants';
import UserStore from '../stores/UserStore';
import TicketSmartCarnet from '../records/smartCarnet/TicketSmartCarnet';
import trainTime from '../lib/TrainTime';

const SEARCH_URL = '/jp/carnet-in-the-cloud';

const getSingleTicketUrl = (customerId, ticketId) =>
    `smartcard/${customerId}/keygo/carnet/tickets/${ticketId}`;

const getTicketsUrl = (customerId, ticketStatus) => {
    let params = '?fulfilmentType=CLOUD';

    switch (ticketStatus) {
        case SmartCarnetTicketStatus.ACTIVE: {
            params += '&sortAsc=true';
            break;
        }

        case SmartCarnetTicketStatus.EXPIRED: {
            const yesterday = trainTime()
                .add(-1, 'day')
                .format('YYYY-MM-DD');
            params += `&validFrom=2020-01-01&validUntil=${yesterday}&expired=true`;
            break;
        }

        case SmartCarnetTicketStatus.ALL: {
            const nextYear = trainTime()
                .add(1, 'year')
                .format('YYYY-MM-DD');
            params += `&validFrom=2020-01-01&validUntil=${nextYear}`;
            break;
        }
    }

    return `/customers/${customerId}/smartcard/keygo/carnet/tickets/${params}`;
};

const getValidatePassesUrl = (customerId, newTickets, originNlc, destinationNlc) =>
    `/smartcard/${customerId}/keygo/carnet/tickets/validate?newTickets=${newTickets}&originNlc=${originNlc}&destinationNlc=${destinationNlc}`;

const SmartCarnetService = {
    async search({ products = 1, search }) {
        const url = SEARCH_URL;

        let response;

        let requestBody = {
            origin: search.origin.nlc,
            destination: search.destination.nlc,
            outward: search.startingDate.format('YYYY-MM-DD'),
            products: parseInt(products),
            searchPreferences: {
                ticketTypes: [SmartCarnetTicketCodes.OFF_PEAK, SmartCarnetTicketCodes.PEAK],
            },
        };

        try {
            response = await APIClient.post(url, requestBody);
        } catch (error) {
            if (_.result(error, 'body.result', []).length) {
                console.error('Supressing error', error);
                response = error.body;
                throw error;
            } else {
                throw error;
            }
        }

        const faresResult = _.result(response, 'result', []);
        const fares = resolveAll(faresResult).as(Fare);
        return fares.sortBy(fare => fare.getPrice());
    },

    async searchReturn({ products = 1, search }) {
        // Swap stations
        const destination = search.origin;
        const origin = search.destination;

        let returnSearch = search.set('destination', destination).set('origin', origin);

        return await SmartCarnetService.search({ search: returnSearch, products });
    },

    async getSingleTicket(ticketId) {
        const user = UserStore.getCurrent();

        if (!user || !user.id) {
            return null;
        }

        const url = user && user.id ? getSingleTicketUrl(user.id, ticketId) : null;

        if (!url) {
            return null;
        }

        let response;

        try {
            response = await IblocksAPIClient.get(url);
            return resolve(response).as(TicketSmartCarnet);
        } catch (error) {
            if (_.result(error, 'body.result', []).length) {
                console.error('Supressing error', error);
                response = error.body;
            } else {
                throw error;
            }
        }

        return null;
    },

    async getTickets({ ticketStatus, user }) {
        if (!user || !user.id) {
            return null;
        }

        let response;

        try {
            response = await IblocksAPIClient.get(getTicketsUrl(user.id, ticketStatus));
            const tickets = resolveAll(response.result).as(TicketSmartCarnet);
            return tickets;
        } catch (error) {
            if (_.result(error, 'body.result', []).length) {
                console.error('Supressing error', error);
                response = error.body;
            } else {
                throw error;
            }
        }

        return null;
    },

    async validatePasses({ destination, origin, newTickets, user }) {
        const url =
            user && user.id
                ? getValidatePassesUrl(user.id, newTickets, origin.nlc, destination.nlc)
                : -1;

        let response;

        try {
            response = await IblocksAPIClient.get(url);

            return response;
        } catch (error) {
            if (_.result(error, 'body.result', []).length) {
                console.error('Supressing error', error);
                response = error.body;
            } else {
                throw error;
            }
        }

        return null;
    },
};

export default SmartCarnetService;



// WEBPACK FOOTER //
// ./src/services/SmartCarnetService.js